import React from "react";
import classNames from "classnames";
import { DateTime } from "luxon";
import { UseCases } from "@planzone/core";
import { Safeguard } from "@scenes/safeguard";
import { Props } from "./types";
import { DateTimeInput } from "./dateTimeInput";
import styles from "./styles.scss";
import {
  event,
  list,
  milestone,
  OptimizedGantt,
  project,
  ProjectElementType,
  task,
} from "@planzone/dto";

function isDateValid(date: string) {
  return DateTime.fromISO(date, { zone: "utc" }).isValid;
}

function formatDateShort(iso: string, zone: string): string {
  const dt = DateTime.fromISO(iso, { zone: "utc" }).setZone(zone);
  return dt.isValid ? dt.toLocaleString(DateTime.DATE_SHORT) : "—";
}

function isProject({ type }: OptimizedGantt[number]) {
  return type === ProjectElementType.PROJECT;
}

function DateForm({
  date,
  projectTimezone,
  recalculatedDate,
  projectElement,
  minDate,
  maxDate,
  disabled,
  isCompletionDate,
  submit,
}: Props) {
  const userTimezone = DateTime.local().zoneName;
  return (
    <Safeguard
      useCases={[
        UseCases.editProject,
        UseCases.editList,
        UseCases.editTask,
        UseCases.editEvent,
        UseCases.editMilestone,
      ]}
      context={
        projectElement.type === ProjectElementType.PROJECT
          ? {
              project: {
                ...project,
                ...projectElement,
              },
            }
          : projectElement.type === ProjectElementType.LIST
            ? {
                list: {
                  ...list,
                  startDate: projectElement.startDate,
                  endDate: projectElement.endDate,
                  uuid: projectElement.uuid,
                },
              }
            : projectElement.type === ProjectElementType.EVENT
              ? {
                  event: {
                    ...event,
                    startTime: projectElement.startDate,
                    endTime: projectElement.endDate,
                    uuid: projectElement.uuid,
                  },
                }
              : projectElement.type === ProjectElementType.MILESTONE
                ? {
                    milestone: {
                      ...milestone,
                      endDate: projectElement.endDate,
                      uuid: projectElement.uuid,
                    },
                  }
                : projectElement.type === ProjectElementType.TASK
                  ? {
                      task: {
                        ...task,
                        startDate: projectElement.startDate,
                        endDate: projectElement.endDate,
                        uuid: projectElement.uuid,
                      },
                    }
                  : {}
      }
    >
      {({
        canEditProject,
        canEditList,
        canEditEvent,
        canEditMilestone,
        canEditTask,
      }) => {
        const canEdit =
          projectElement.type === ProjectElementType.PROJECT
            ? canEditProject
            : projectElement.type === ProjectElementType.LIST
              ? canEditList
              : projectElement.type === ProjectElementType.EVENT
                ? canEditEvent
                : projectElement.type === ProjectElementType.MILESTONE
                  ? canEditMilestone
                  : projectElement.type === ProjectElementType.TASK
                    ? canEditTask
                    : false;

        return (
          <div>
            {!canEdit ? (
              <div
                className={classNames(styles.dateForm, styles.readOnly, {
                  [styles.completion]: isCompletionDate,
                  [styles.completionEditable]: isCompletionDate,
                })}
              >
                <p
                  className={classNames(styles.input, {
                    [styles.recalculated]: !!recalculatedDate && !date,
                  })}
                >
                  {recalculatedDate
                    ? !!date
                      ? formatDateShort(recalculatedDate, userTimezone)
                      : `(${formatDateShort(recalculatedDate, userTimezone)})`
                    : "-"}
                </p>
              </div>
            ) : (
              <div
                className={classNames(styles.dateForm, {
                  [styles.completion]: isCompletionDate,
                  [styles.completionEditable]: isCompletionDate,
                })}
              >
                <DateTimeInput
                  dateTime={date || ""}
                  projectTimezone={projectTimezone}
                  placeholder={
                    recalculatedDate
                      ? !!date
                        ? formatDateShort(recalculatedDate, userTimezone)
                        : `(${formatDateShort(recalculatedDate, userTimezone)})`
                      : "-"
                  }
                  minDate={minDate}
                  maxDate={maxDate}
                  isDisabled={disabled}
                  readOnly={!canEdit}
                  input={(dateTime) => !disabled && submit(dateTime)}
                />
              </div>
            )}
          </div>
        );
      }}
    </Safeguard>
  );
}

export { DateForm };
